// Web Audio API synthesized sound effects (0 kB external files, zero latency)
import { useSettingsStore } from '@/stores/settingsStore';

let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  try {
    const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtxClass) return null;
    if (!audioCtx || audioCtx.state === 'closed') {
      audioCtx = new AudioCtxClass();
    }
    // Synchronously resume if suspended or interrupted
    if (audioCtx.state === 'suspended' || (audioCtx.state as string) === 'interrupted') {
      audioCtx.resume().catch(() => {});
    }
    return audioCtx;
  } catch {
    return null;
  }
}

// Global unlock listener: WebKit (iOS Safari, Telegram WebApp) requires
// playing a 1-sample silent buffer on user gesture to fully activate audio hardware.
if (typeof window !== 'undefined') {
  const unlockAudio = () => {
    try {
      const ctx = getAudioContext();
      if (ctx) {
        if (ctx.state === 'suspended' || (ctx.state as string) === 'interrupted') {
          ctx.resume().catch(() => {});
        }
        if (ctx.state === 'running') {
          return;
        }
        // iOS WebKit silent 1-sample buffer unlock
        const buffer = ctx.createBuffer(1, 1, 22050);
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.start(0);
      }
    } catch {
      // ignore
    }
  };

  ['click', 'touchstart', 'touchend', 'pointerdown', 'keydown'].forEach(evt => {
    window.addEventListener(evt, unlockAudio, { capture: true, passive: true });
  });
}

/**
 * Вариант 1 («Duolingo Chime»):
 * Полифоническое мажорное арпеджио из 4 нот с кристальным обертоном.
 * Тональность плавно масштабируется при высоком комбо-стрике.
 * @param combo - множитель серии комбо
 * @param force - воспроизвести принудительно (для кнопки Тест в настройках)
 */
export function playSuccessSound(combo = 1, force = false) {
  try {
    // Если настройка undefined (старый кэш в localStorage), считаем включенной по умолчанию
    const isEnabled = force || useSettingsStore.getState().soundEffectsEnabled !== false;
    if (!isEnabled) return;

    const ctx = getAudioContext();
    if (!ctx) return;

    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    const now = ctx.currentTime;
    const baseFreq = 523.25 * (1 + Math.max(0, combo - 1) * 0.04);
    const notes = [baseFreq, baseFreq * 1.2599, baseFreq * 1.4983, baseFreq * 2.0];

    notes.forEach((freq, idx) => {
      const t = now + idx * 0.042;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, t);

      // Кристальный обертон для звонкости и чистоты звучания
      const overtone = ctx.createOscillator();
      const overGain = ctx.createGain();
      overtone.type = 'sine';
      overtone.frequency.setValueAtTime(freq * 2, t);

      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.linearRampToValueAtTime(0.24, t + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.38);

      overGain.gain.setValueAtTime(0.0001, t);
      overGain.gain.linearRampToValueAtTime(0.08, t + 0.008);
      overGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);

      osc.connect(gain);
      overtone.connect(overGain);
      gain.connect(ctx.destination);
      overGain.connect(ctx.destination);

      osc.start(t);
      osc.stop(t + 0.39);
      overtone.start(t);
      overtone.stop(t + 0.23);
    });
  } catch {
    // Безопасно игнорируем любые ошибки аудио подсистемы
  }
}

/**
 * Вариант 2 («Muted Woodblock»):
 * Сухой тактильный щелчок деревянной плашки (короткий и ненавязчивый).
 * @param force - воспроизвести принудительно (для кнопки Тест)
 */
export function playErrorSound(force = false) {
  try {
    const isEnabled = force || useSettingsStore.getState().soundEffectsEnabled !== false;
    if (!isEnabled) return;

    const ctx = getAudioContext();
    if (!ctx) return;

    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(280, now);
    osc.frequency.exponentialRampToValueAtTime(75, now + 0.07);

    gain.gain.setValueAtTime(0.32, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.09);
  } catch {
    // Безопасно игнорируем
  }
}
